import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Box, Divider, Drawer, Fade, Stack, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';
import { useEffect, useMemo, useState } from 'react';
import { StringUtils } from '../utils/StringUtils';
import { BookmarkButton } from './BookmarkButton';
import { HelpButton } from './HelpButton';
import { ModuleButton } from './ModuleButton';
import { NotifButton } from './NotifButton';
import { OrgIcon } from './OrgIcon';
import { ProfileButton } from './profile/ProfileButton';
import { SideNavItem } from './SideNavItem';

export const SIDE_RAIL_WIDTH = 88;
export const DRAWER_WIDTH = 240;

export const SideNav = ({ children }) => {
  const module = useModuleFromRoute();
  const { hasRole, org } = usePrincipal();
  const rsrc = useRsrc();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSidenavMenu, setActiveSidenavMenu] = useState(null);
  const [moduleIdToSideNavFooter, setModuleIdToSideNavFooter] = useState({});

  const filterSidenavMenus = (sidenavMenus, moduleLevel = false) => {
    if (!sidenavMenus) return null;

    return sidenavMenus
      .map((sidenavMenu) => {
        sidenavMenu.children = filterSidenavMenus(sidenavMenu.children);

        if (moduleLevel && sidenavMenu.role) {
          // At module level, hide the entire module if the user doesn't have the role
          return hasRole(sidenavMenu.role) ? sidenavMenu : null;
        } else if (sidenavMenu?.children?.length > 0) {
          // Show if it has visible children, regardless of its own role
          return sidenavMenu;
        }
        // If the user has the required role (if any)
        return hasRole(sidenavMenu.role) ? sidenavMenu : null;
      })
      .filter(Boolean);
  };

  /** merge two nodes that share the same id */
  const mergeNode = (first = {}, second) => {
    const merged = { ...first, ...second }; // second wins on scalar keys

    // merge children (if either side has them)
    if (first.children || second.children) {
      const combined = [...(first.children || []), ...(second.children || [])];
      const byId = combined.reduce((map, child) => {
        map[child.id] = mergeNode(map[child.id], child);

        return map;
      }, {});
      merged.children = Object.values(byId).sort((a, b) => (a.precedence || 0) - (b.precedence || 0));
    }

    return merged;
  };

  const moduleSidenavMenus = useMemo(() => {
    // require.context runs at build time to build the SideNav JSON
    const sidenavContext = import.meta.glob('/src/**/*.sidenav.js', { eager: true });
    let rawSidenavMenus = Object.keys(sidenavContext)
      .filter((key) => {
        if (!key.includes('/org/')) return true;
        // This is the single chokepoint to remove any sideNavs that are org-specific and not part of this org
        return key.includes(`/${org.toLowerCase()}/`);
      })
      .sort((a, b) => {
        // Sort org-specific routes last so they override shared routes during merge
        return a.includes('/org/') - b.includes('/org/');
      })
      .map((key) => {
        const module = sidenavContext[key];
        return module.default;
      });

    rawSidenavMenus = filterSidenavMenus(rawSidenavMenus, true);

    // top‑level coalesce
    const byId = rawSidenavMenus.reduce((map, nav) => {
      map[nav.id] = mergeNode(map[nav.id], nav);
      return map;
    }, {});

    return Object.values(byId).sort((a, b) => (a.precedence || 0) - (b.precedence || 0));
  }, [org]);

  const toggleDrawerOpen = () => {
    setDrawerOpen((prev) => !prev);
  };

  useEffect(() => {
    rsrc.data('admin', 'MODULE_INFO_DATA_PLUGIN', {}).then((res) => {
      if (res) setModuleIdToSideNavFooter(res);
    });
  }, []);

  const buildSummary = activeSidenavMenu ? moduleIdToSideNavFooter[activeSidenavMenu.id] : null;

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        height: '100vh',
        backgroundColor: theme.palette.background.default,
      })}
    >
      {/* Side Rail */}
      <Stack
        flexDirection="column"
        alignItems="center"
        gap={2}
        sx={(theme) => ({
          width: SIDE_RAIL_WIDTH,
          minWidth: SIDE_RAIL_WIDTH,
          bgcolor: theme.palette.surface.container,
          zIndex: 1300,
          pt: 2,
          pb: 2,
          borderRight: '1px solid',
          borderColor: 'divider',
        })}
      >
        {/* Top: Always visible (Dashboard/OrgIcon) */}
        <Box sx={{ alignItems: 'center' }}>
          {/* Organization Icon */}
          <OrgIcon />
        </Box>

        {/* Middle: Scrollable module buttons */}
        <Stack gap={2} sx={{ alignItems: 'center', flexGrow: 1, overflowY: 'auto' }}>
          {/* Module Buttons */}
          {moduleSidenavMenus.map((menu) => (
            <ModuleButton
              key={menu.id}
              menu={menu}
              isActive={module === menu.id}
              onClick={toggleDrawerOpen}
              setActiveSidenavMenu={setActiveSidenavMenu}
            />
          ))}
        </Stack>

        {/* Bottom: Always visible buttons */}
        <Stack direction="column" alignItems="center" gap={2}>
          {/* Placeholder for options */}
          <HelpButton />
          <NotifButton />
          <BookmarkButton />
          <ProfileButton />
        </Stack>
      </Stack>

      {/* Drawer for Module Details */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={drawerOpen}
        slotProps={{
          paper: {
            sx: (theme) => ({
              width: DRAWER_WIDTH,
              left: SIDE_RAIL_WIDTH, // offset the drawer so it doesn’t cover the side rail
              height: '100%',
              bgcolor: theme.palette.surface.container,
              borderTopRightRadius: '12px',
              borderBottomRightRadius: '12px',
              boxShadow: 2,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }),
          },
        }}
      >
        {activeSidenavMenu && (
          <Fade in={!!activeSidenavMenu} key={activeSidenavMenu.id} timeout={500}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Box sx={{ mb: 2, fontWeight: 'bold' }}>{activeSidenavMenu.displayName}</Box>
                <SimpleTreeView>
                  {activeSidenavMenu.children &&
                    activeSidenavMenu.children.map((child) => <SideNavItem key={child.id} node={child} />)}
                </SimpleTreeView>
              </Box>

              <Box sx={{ mt: 'auto', pt: 2, flexShrink: 0 }}>
                <Divider sx={{ mb: 1 }} />
                <Stack gap={1}>
                  <Typography variant="body2">Version {buildSummary?.version}</Typography>
                  <Typography variant="body2">
                    Built at {StringUtils.toLocalInstant(buildSummary?.buildTime)}
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Fade>
        )}
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={(theme) => ({
          flexGrow: 1,
          p: 2,
          ml: `${drawerOpen && activeSidenavMenu ? DRAWER_WIDTH : 0}px`,
          transition: 'margin 0.3s ease',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        })}
      >
        {children}
      </Box>
    </Box>
  );
};
